import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface WorkwearShopProps {
  provider?: any;
  onPurchaseComplete?: () => void;
}

interface CartItem {
  id: number;
  name: string;
  price: number;
  size: string;
  quantity: number;
  image: string;
}

export default function WorkwearShop({ provider, onPurchaseComplete }: WorkwearShopProps) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState<{ [key: number]: string }>({});
  const [kitConfig, setKitConfig] = useState<any>(null);
  const [loadingKit, setLoadingKit] = useState(true);

  // Check if provider has purchased starter kit
  const hasStarterKit = provider?.workwear_purchased === true;

  useEffect(() => {
    fetchKitConfig();
  }, []);

  const fetchKitConfig = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-workwear-kit-config`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ kit_type: 'registration_kit' })
        }
      );

      const data = await response.json();
      if (data.error) throw new Error(data.error);
      
      setKitConfig(data);
    } catch (error) {
      console.error('Error fetching kit config:', error);
    } finally {
      setLoadingKit(false);
    }
  };

  const products = [
    {
      id: 1,
      name: 'Professional Polo Shirt',
      price: 24.99,
      image: 'https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/7a990eb4c3b9293f1d275d63314584dc.png',
      description: 'Professional polo shirt for a smart, comfortable work appearance',
      sizes: ['S', 'M', 'L', 'XL', 'XXL']
    },
    {
      id: 2,
      name: 'Professional Bodywarmer',
      price: 57.49,
      image: 'https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/f3ada3b1722349e6e30aa9b863851feb.png',
      description: 'Lightweight bodywarmer for extra warmth without restricting movement',
      sizes: ['S', 'M', 'L', 'XL', 'XXL']
    },
    {
      id: 3,
      name: 'Fleece Jacket',
      price: 49.99,
      image: 'https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/bf9ee8f1edc8fe461226a41ae63b8c9f.png',
      description: 'Comfortable fleece jacket for warmth and flexibility on the job',
      sizes: ['S', 'M', 'L', 'XL', 'XXL']
    },
    {
      id: 4,
      name: 'Hooded Sweatshirt',
      price: 41.24,
      image: 'https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/3ef618f70a322f03c878c8865bdc021d.png',
      description: 'Comfortable hooded sweatshirt for casual work and layering',
      sizes: ['S', 'M', 'L', 'XL', 'XXL']
    },
    {
      id: 5,
      name: 'Professional Jacket',
      price: 94.99,
      image: 'https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/7443ac20df8f16f10b0a13c0b9b75096.png',
      description: 'Premium professional jacket for all-weather protection and comfort',
      sizes: ['S', 'M', 'L', 'XL', 'XXL']
    },
    {
      id: 6,
      name: 'Sweatshirt',
      price: 34.99,
      image: 'https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/95be459e905af1ff8bc13ab73680a527.png',
      description: 'Comfortable sweatshirt for everyday work and casual wear',
      sizes: ['S', 'M', 'L', 'XL', 'XXL']
    },
    {
      id: 7,
      name: 'T-Shirt',
      price: 18.74,
      image: 'https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/a829b25173ee4d88b80847a6971e8376.png',
      description: 'Classic comfortable t-shirt for everyday work wear',
      sizes: ['S', 'M', 'L', 'XL', 'XXL']
    },
    {
      id: 8,
      name: 'Work Trousers',
      price: 43.74,
      image: 'https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/b1c99911335555c278d9c971251f719c.png',
      description: 'Durable work trousers designed for comfort and functionality',
      sizes: ['28', '30', '32', '34', '36', '38', '40']
    }
  ];

  const addToCart = (product: typeof products[0]) => {
    // Check if starter kit is required
    if (!hasStarterKit) {
      alert('You must purchase the Complete Starter Kit first before buying individual items.');
      return;
    }

    const selectedSize = selectedSizes[product.id] || product.sizes[0];
    
    const existingItemIndex = cart.findIndex(
      item => item.id === product.id && item.size === selectedSize
    );

    if (existingItemIndex > -1) {
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += 1;
      setCart(updatedCart);
    } else {
      setCart([...cart, {
        id: product.id,
        name: product.name,
        price: product.price,
        size: selectedSize,
        quantity: 1,
        image: product.image
      }]);
    }
    
    setShowCart(true);
  };

  const removeFromCart = (index: number) => {
    const updatedCart = cart.filter((_, i) => i !== index);
    setCart(updatedCart);
  };

  const updateQuantity = (index: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    const updatedCart = [...cart];
    updatedCart[index].quantity = newQuantity;
    setCart(updatedCart);
  };

  const cartSubtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartVAT = cartSubtotal * 0.20;
  const cartTotal = cartSubtotal + cartVAT;
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = async () => {
    if (!provider) {
      alert('Provider information not found');
      return;
    }

    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      const itemsList = cart.map(item => 
        `${item.name} (Size: ${item.size}) x${item.quantity}`
      ).join(', ');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-workwear-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            type: 'individual_items',
            items: cart,
            totalAmount: cartTotal,
            subtotal: cartSubtotal,
            vat: cartVAT,
            customerEmail: provider.email,
            customerName: `${provider.first_name} ${provider.last_name}`,
            providerId: provider.id,
            itemsList: itemsList,
            successUrl: `${window.location.origin}/provider-dashboard?workwear_success=true`,
            cancelUrl: `${window.location.origin}/provider-dashboard?workwear_cancelled=true`
          })
        }
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert(`Failed to process checkout: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseKit = async () => {
    if (!provider) {
      alert('Provider information not found');
      return;
    }

    if (!kitConfig) {
      alert('Kit configuration not loaded');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-workwear-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            type: 'starter_kit',
            totalAmount: kitConfig.total_price,
            customerEmail: provider.email,
            customerName: `${provider.first_name} ${provider.last_name}`,
            providerId: provider.id,
            successUrl: `${window.location.origin}/provider-dashboard?workwear_success=true`,
            cancelUrl: `${window.location.origin}/provider-dashboard?workwear_cancelled=true`
          })
        }
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error: any) {
      console.error('Purchase error:', error);
      alert(`Failed to process purchase: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingKit) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-6xl text-teal-600 mb-4"></i>
          <p className="text-gray-600">Loading workwear shop...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Workwear Shop</h1>
          <p className="text-gray-600 mt-1">Get your professional workwear and equipment</p>
        </div>
        
        {/* Cart Button - Only show if starter kit purchased */}
        {hasStarterKit && (
          <button
            onClick={() => setShowCart(!showCart)}
            className="relative px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className="ri-shopping-cart-line text-xl"></i>
            Cart
            {cartItemCount > 0 && (
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                {cartItemCount}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Cart Sidebar */}
      {showCart && hasStarterKit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end" onClick={() => setShowCart(false)}>
          <div 
            className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Your Cart</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-shopping-cart-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">Your cart is empty</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item, index) => (
                      <div key={index} className="flex gap-4 bg-gray-50 rounded-lg p-4">
                        <div className="w-20 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 mb-1">{item.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">Size: {item.size}</p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(index, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-50 cursor-pointer"
                            >
                              <i className="ri-subtract-line"></i>
                            </button>
                            <span className="w-12 text-center font-medium">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(index, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-50 cursor-pointer"
                            >
                              <i className="ri-add-line"></i>
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-col items-end justify-between">
                          <button
                            onClick={() => removeFromCart(index)}
                            className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-xl"></i>
                          </button>
                          <p className="font-bold text-teal-600">£{(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4 space-y-3 mb-6">
                    <div className="flex justify-between text-gray-700">
                      <span>Subtotal:</span>
                      <span className="font-medium">£{cartSubtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>VAT (20%):</span>
                      <span className="font-medium">£{cartVAT.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold text-gray-900 border-t pt-3">
                      <span>Total:</span>
                      <span className="text-teal-600">£{cartTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  <button
                    onClick={handleCheckout}
                    disabled={loading}
                    className="w-full py-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:from-teal-700 hover:to-emerald-700 transition-all duration-300 font-bold text-lg whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin text-2xl"></i>
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-secure-payment-line text-2xl"></i>
                        Proceed to Checkout
                      </span>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Required Kit Banner - Show if not purchased */}
      {!hasStarterKit && (
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-6">
          <div className="flex items-start gap-4">
            <i className="ri-alert-line text-3xl"></i>
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-2">Starter Kit Required</h2>
              <p className="opacity-90">
                You must purchase the Complete Starter Kit first before you can buy individual items. This ensures all providers have professional workwear to represent Taskbudy.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Starter Kit - Featured */}
      {!hasStarterKit && kitConfig && (
        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-300 rounded-2xl p-8 shadow-lg">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="inline-block px-4 py-1 bg-orange-500 text-white rounded-full text-sm font-bold mb-3">
                REQUIRED FIRST PURCHASE
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{kitConfig.kit_name}</h2>
              <p className="text-gray-700 text-lg">{kitConfig.kit_description}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Included Items:</h3>
              <ul className="space-y-2">
                {kitConfig.items.map((item: any, index: number) => (
                  <li key={index} className="flex items-center gap-2 text-gray-700">
                    <i className="ri-check-line text-teal-600 text-xl"></i>
                    <span>{item.name} {item.quantity > 1 && `(${item.quantity}x)`}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Kit Price:</span>
                  <span className="text-gray-900 font-bold">£{kitConfig.base_price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">VAT (20%):</span>
                  <span className="text-gray-900 font-bold">£{kitConfig.vat_amount.toFixed(2)}</span>
                </div>
                <div className="border-t-2 pt-3 flex justify-between items-center">
                  <span className="text-xl font-bold text-gray-900">Total:</span>
                  <span className="text-3xl font-bold text-teal-600">£{kitConfig.total_price.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handlePurchaseKit}
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:from-teal-700 hover:to-emerald-700 transition-all duration-300 font-bold text-lg whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin text-2xl"></i>
                Processing...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-shopping-cart-line text-2xl"></i>
                Purchase Starter Kit - £{kitConfig.total_price.toFixed(2)}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Individual Items - Only show if starter kit purchased */}
      {hasStarterKit && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-green-600 text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Additional Items</h2>
              <p className="text-gray-600">Starter kit purchased - you can now buy individual items</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((item) => (
              <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow">
                <div className="w-full h-64 bg-gray-100">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{item.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-teal-600">£{item.price.toFixed(2)}</span>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
                    <select
                      value={selectedSizes[item.id] || item.sizes[0]}
                      onChange={(e) => setSelectedSizes({ ...selectedSizes, [item.id]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                    >
                      {item.sizes.map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => addToCart(item)}
                    className="w-full py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-shopping-cart-line"></i>
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locked Individual Items - Show if starter kit not purchased */}
      {!hasStarterKit && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Additional Items</h2>
          <p className="text-gray-600 mb-6">Available after purchasing the starter kit</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((item) => (
              <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden opacity-60 relative">
                <div className="absolute inset-0 bg-gray-900 bg-opacity-40 z-10 flex items-center justify-center">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <i className="ri-lock-line text-3xl text-gray-700 mb-2"></i>
                    <p className="font-bold text-gray-900">Purchase Starter Kit First</p>
                  </div>
                </div>
                <div className="w-full h-64 bg-gray-100">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{item.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-teal-600">£{item.price.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}